import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const Form = styled.form`
  display: grid;
  gap: 14px;
  width: 100%;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 220px) minmax(0, 1fr);
  align-items: center;
  gap: 14px;
  width: 100%;

  @media only screen and (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const Label = styled.label`
  margin: 0;
  color: #18233b;
  font: 600 14px/1.4 Poppins, sans-serif;
`;

const FieldWrap = styled.div`
  width: 100%;
  min-width: 0;
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  min-height: 46px;
  box-sizing: border-box;
  border: 1px solid rgba(0, 24, 126, 0.16);
  border-radius: 14px;
  padding: 12px 14px;
  background: #ffffff;
  color: #18233b;
  font: 500 14px/1.4 Poppins, sans-serif;
`;

const Select = styled.select`
  width: 100%;
  min-width: 0;
  min-height: 46px;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 1px solid rgba(0, 24, 126, 0.16);
  border-radius: 14px;
  padding: 12px 44px 12px 14px;
  background: #fff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%2352607c' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M1 1.25 6 6.25l5-5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 18px center;
  background-size: 12px 8px;
  color: #18233b;
  font: 500 14px/1.4 Poppins, sans-serif;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 4px;
`;

const SavedNote = styled.span`
  color: #1f7a54;
  font: 600 13px/1.4 Poppins, sans-serif;
`;

const SaveButton = styled.button`
  min-height: 46px;
  padding: 12px 28px;
  border: 0;
  border-radius: 14px;
  background: #00187e;
  color: #ffffff;
  font: 600 14px/1.4 Poppins, sans-serif;
  cursor: pointer;

  &:disabled {
    background: rgba(0, 24, 126, 0.25);
    cursor: default;
  }
`;

const COUNTRY_OPTIONS = [
  { value: "", label: "—" },
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "MX", label: "Mexico" },
  { value: "GB", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "ES", label: "Spain" },
  { value: "IT", label: "Italy" },
  { value: "NL", label: "Netherlands" },
  { value: "JP", label: "Japan" },
  { value: "BR", label: "Brazil" },
];

export const FormAppConfiguration = ({
  proshopLabel,
  proshopName,
  proshopAddress,
  proshopCity,
  proshopState,
  proshopPostalCode,
  proshopCountry,
  proshopPhone,
  proshopEmail,
  addresLabel,
  cityLabel,
  stateLabel,
  postalCodeLabel,
  countryLabel,
  phoneLabel,
  emailLabel,
  saveLabel = "Save",
  savedLabel = "Saved",
  onSubmit,
}) => {
  // The form keeps its own values and only reports them on save. It used to
  // write through on every keystroke, which had two problems: the inputs were
  // debounced with minLength 3, so a two-letter province was never reported at
  // all, and every field was echoed back from the server round-trip, so a save
  // that returned the old value silently erased what had just been typed.
  const fieldsFromProps = useMemo(
    () => ({
      name: proshopName || "",
      address: proshopAddress || "",
      city: proshopCity || "",
      state: proshopState || "",
      postal_code: proshopPostalCode || "",
      country: proshopCountry || "",
      phone: proshopPhone || "",
      email: proshopEmail || "",
    }),
    [
      proshopName,
      proshopAddress,
      proshopCity,
      proshopState,
      proshopPostalCode,
      proshopCountry,
      proshopPhone,
      proshopEmail,
    ]
  );

  const [values, setValues] = useState(fieldsFromProps);
  const [dirty, setDirty] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // Take server values back only while the user has nothing unsaved, so an
  // incoming refresh never overwrites what they are in the middle of typing.
  useEffect(() => {
    if (!dirty) {
      setValues(fieldsFromProps);
    }
  }, [fieldsFromProps, dirty]);

  const setField = (field) => (event) => {
    const { value } = event.target;
    setValues((current) => ({ ...current, [field]: value }));
    setDirty(true);
    setJustSaved(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (onSubmit) {
      onSubmit(values);
    }

    setDirty(false);
    setJustSaved(true);
  };

  const textField = (id, label, field, type = "text") => (
    <Row>
      <Label htmlFor={id}>{label}</Label>
      <FieldWrap>
        <Input
          className="form-control"
          id={id}
          type={type}
          name={field}
          value={values[field]}
          onChange={setField(field)}
        />
      </FieldWrap>
    </Row>
  );

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        {textField("proshopName", proshopLabel, "name")}
        {textField("address", addresLabel, "address")}
        {textField("city", cityLabel, "city")}
        {textField("state", stateLabel, "state")}
        {textField("postal_code", postalCodeLabel, "postal_code")}
        <Row>
          <Label htmlFor="country">{countryLabel}</Label>
          <FieldWrap>
            <Select
              id="country"
              name="country"
              value={values.country}
              onChange={setField("country")}
            >
              {COUNTRY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FieldWrap>
        </Row>
        {textField("phone", phoneLabel, "phone", "tel")}
        {textField("email", emailLabel, "email", "email")}
        <Actions>
          {justSaved && !dirty ? <SavedNote>{savedLabel}</SavedNote> : null}
          <SaveButton type="submit" disabled={!dirty}>
            {saveLabel}
          </SaveButton>
        </Actions>
      </Form>
    </Container>
  );
};
